/**
 * CurrentActionPanel Component
 * Feature: 003-c-program-files - Send Action to Device
 * User Story 3: View and Manage Current Action from Controls
 * BA-58: an action with "stay until parent approves" waits here for the parent's Approve
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  AlertTitle,
  Box,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Skeleton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Check as CheckIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ImageOutlined as ImageIcon,
  PlayArrow as PlayArrowIcon,
  WifiOff as WifiOffIcon,
} from '@mui/icons-material';
import { useCurrentAction } from '../hooks/useCurrentAction';
import { useDeviceLastSeen } from '../hooks/useDeviceLastSeen';
import { useDevice } from '../contexts/DeviceContext';
import { currentActionService, CurrentActionApiError } from '../services/currentActionService';
import { formatClockTime, formatRelativeTime } from '../utils/timestampUtils';
import { CurrentAction, isAwaitingApproval } from '../types/currentAction';
import { SendActionDialog } from './SendActionDialog';
import { ActionSelector } from './ActionSelector';
import { Action } from '../types/action';
import { actionsService } from '../services/actionsService';
import { useTranslation } from 'react-i18next';

/** The tablet's heartbeat is every 5 min: after 10 min without one it counts as offline */
export const OFFLINE_AFTER_MS = 10 * 60 * 1000;

/** (actionId, startTimestamp) identifies a sent action; 'none' is the empty panel */
const identityOf = (action: CurrentAction | null): string =>
  action ? `${action.actionId}@${action.startTimestamp}` : 'none';

/**
 * A line about something that happened to an action (approved, removed, ended elsewhere,
 * replaced). It shows on whatever the panel moves to next (`at`: the empty state, or the
 * newly sent action) and stays until the panel changes again (BA-58 P6).
 */
interface Notice {
  key: string;
  severity: 'success' | 'info';
  /** The action it is about */
  from: string;
  /** Where it shows; unset until the panel has moved on from `from` */
  at?: string;
}

interface CurrentActionPanelProps {
  deviceId: string;
  actions: Action[]; // List of actions to lookup action name
  onReloadActions?: () => void;
}

export const CurrentActionPanel: React.FC<CurrentActionPanelProps> = ({
  deviceId,
  actions,
  onReloadActions,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentAction, loading, error } = useCurrentAction(deviceId);
  const lastSeen = useDeviceLastSeen(deviceId);
  const { selectedDevice } = useDevice();
  const deviceName = selectedDevice?.id === deviceId ? selectedDevice.name : '';
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string>('');
  const [relativeTime, setRelativeTime] = useState<string>('');
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [now, setNow] = useState(() => Date.now());

  const identity = identityOf(currentAction);
  const waiting = isAwaitingApproval(currentAction);
  const offline = lastSeen !== null && now - lastSeen > OFFLINE_AFTER_MS;
  const identityRef = useRef(identity);
  identityRef.current = identity;
  // The action this tab approved or removed, so its end is not "ended elsewhere"
  const actedOnRef = useRef<string | null>(null);
  const previousRef = useRef<{ identity: string; waiting: boolean } | null>(null);

  // Another device, or the panel opened again: nothing carries over
  useEffect(() => {
    setNotice(null);
    setApproveError(false);
    actedOnRef.current = null;
    previousRef.current = null;
  }, [deviceId]);

  // The panel moved to another action (or to none): place or drop the line
  useEffect(() => {
    if (loading) {
      return;
    }
    const previous = previousRef.current;
    previousRef.current = { identity, waiting };
    if (!previous || previous.identity === identity) {
      return;
    }
    setApproveError(false);
    if (identity === 'none' && previous.waiting && actedOnRef.current !== previous.identity) {
      setNotice({ key: 'currentAction.endedElsewhere', severity: 'info', from: previous.identity, at: 'none' });
      return;
    }
    setNotice((n) => {
      if (!n) return n;
      if (n.at === undefined) return identity !== n.from ? { ...n, at: identity } : n;
      return identity === n.at ? n : null;
    });
  }, [identity, waiting, loading]);

  const showNotice = (key: string, severity: Notice['severity'], from: string) => {
    const current = identityRef.current;
    setNotice({ key, severity, from, at: current !== from ? current : undefined });
  };

  // "Is the tablet offline" is a matter of time passing too
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  // Image loading state
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [imageError, setImageError] = useState(false);

  // ActionSelector and SendActionDialog state for empty state
  const [actionSelectorOpen, setActionSelectorOpen] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectedActionForSend, setSelectedActionForSend] = useState<Action | null>(null);

  // Update relative time every 30 seconds
  useEffect(() => {
    if (currentAction) {
      setRelativeTime(formatRelativeTime(currentAction.startTimestamp, t));

      const interval = setInterval(() => {
        setRelativeTime(formatRelativeTime(currentAction.startTimestamp, t));
      }, 30000); // Update every 30 seconds

      return () => clearInterval(interval);
    }
  }, [currentAction, t]);

  // Find action details
  const action = currentAction
    ? actions.find((a) => a.id === currentAction.actionId)
    : null;

  // Fetch action image using signed URL
  useEffect(() => {
    let isMounted = true;

    const fetchImageUrl = async () => {
      // Check if action has any picture (thumbnail or original)
      if (!currentAction || (!action?.thumbnailUrl && !action?.pictureUrl)) {
        setImageLoading(false);
        return;
      }

      try {
        setImageLoading(true);
        setImageError(false);

        // Get signed URL for thumbnail (preferred) or original
        const hasThumbnail = !!action?.thumbnailUrl;
        const response = await actionsService.getSignedPictureUrl(
          deviceId,
          currentAction.actionId,
          hasThumbnail
        );

        if (isMounted) {
          setImageUrl(response.url);
          setImageLoading(false);
        }
      } catch (error) {
        console.error('Error fetching image URL:', error);
        if (isMounted) {
          setImageError(true);
          setImageLoading(false);
        }
      }
    };

    fetchImageUrl();

    return () => {
      isMounted = false;
    };
  }, [action?.id, action?.pictureUrl, action?.thumbnailUrl, currentAction?.actionId, deviceId]);

  const handleEditClick = () => {
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
  };

  const handleRemoveClick = () => {
    setDeleteDialogOpen(true);
    setDeleteError('');
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteConfirm = async () => {
    const from = identity;
    setDeleting(true);
    setDeleteError('');
    setDeleteDialogOpen(false);
    actedOnRef.current = from;

    try {
      await currentActionService.removeAction(deviceId);
      showNotice('currentAction.actionRemovedSuccess', 'success', from);
    } catch (error) {
      actedOnRef.current = null;
      console.error('Failed to remove current action:', error);
      setDeleteError(t('currentAction.failedToRemoveAction'));
    } finally {
      setDeleting(false);
    }
  };

  const handleApprove = async () => {
    if (!currentAction || approving) {
      return;
    }
    const from = identity;
    const wasOffline = offline;
    actedOnRef.current = from;
    setApproving(true);
    setApproveError(false);

    try {
      await currentActionService.approveAction(deviceId, currentAction);
      showNotice(wasOffline ? 'currentAction.approvedOffline' : 'currentAction.approvedSuccess', 'success', from);
    } catch (err) {
      const status = err instanceof CurrentActionApiError ? err.status : 0;
      if (status === 404) {
        // Another tab or parent was first: it has ended, which is what was wanted
        showNotice('currentAction.alreadyEnded', 'info', from);
      } else if (status === 409) {
        showNotice('currentAction.approveConflict', 'info', from);
      } else {
        actedOnRef.current = null;
        setApproveError(true);
      }
    } finally {
      setApproving(false);
    }
  };

  const noticeLine = (at: string) =>
    notice && notice.at === at ? (
      <Alert severity={notice.severity} sx={{ mb: 2 }}>
        {t(notice.key, { device: deviceName })}
      </Alert>
    ) : null;

  // The device's full name, so the parent knows which tablet this is (BA-58 R4)
  const panelTitle = (
    <Box minWidth={0}>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Typography variant="h6">{t('currentAction.title')}</Typography>
        {waiting && <Chip label={t('currentAction.awaitingChip')} color="warning" size="small" />}
      </Box>
      {deviceName && (
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {deviceName}
        </Typography>
      )}
    </Box>
  );

  // Loading state
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={24} />
            <Typography>{t('currentAction.loadingCurrentAction')}</Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            {t('currentAction.failedToLoadCurrentAction', { error: error.message })}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Handler for action selection from ActionSelector
  const handleActionSelected = (actionId: string) => {
    const action = actions.find((a) => a.id === actionId);
    if (action) {
      setSelectedActionForSend(action);
      setActionSelectorOpen(false);
      setSendDialogOpen(true);
    }
  };

  // Empty state (no current action)
  if (!currentAction) {
    return (
      <>
        <Card>
          <CardContent>
            <Box mb={1}>{panelTitle}</Box>
            {noticeLine('none')}
            <Typography color="text.secondary">
              {t('currentAction.noActiveAction')}
            </Typography>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              onClick={() => setActionSelectorOpen(true)}
              fullWidth
            >
              {t('currentAction.selectAction')}
            </Button>
          </CardContent>
        </Card>

        {/* ActionSelector Dialog */}
        <ActionSelector
          open={actionSelectorOpen}
          onClose={() => setActionSelectorOpen(false)}
          deviceId={deviceId}
          actions={actions}
          onActionSelected={handleActionSelected}
          onReloadActions={onReloadActions}
        />

        {/* SendActionDialog for selected action */}
        {selectedActionForSend && (
          <SendActionDialog
            open={sendDialogOpen}
            onClose={() => {
              setSendDialogOpen(false);
              setSelectedActionForSend(null);
            }}
            deviceId={deviceId}
            action={selectedActionForSend}
          />
        )}
      </>
    );
  }

  // Current action exists - display it
  return (
    <>
      <Card>
        <CardContent>
          {/* Header with action buttons. On a phone the buttons get their own full-width
              row, 48 px tall, wrapping when they don't fit (HU) */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexDirection={isPhone ? 'column' : 'row'}
            gap={isPhone ? 1.5 : 2}
            mb={2}
          >
            {panelTitle}
            <Box
              display="flex"
              gap={1}
              flexWrap="wrap"
              justifyContent="flex-end"
              sx={isPhone ? { width: '100%' } : { flexShrink: 0 }}
            >
              {waiting ? (
                <Button
                  size={isPhone ? 'large' : 'medium'}
                  variant="contained"
                  startIcon={approving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon />}
                  onClick={handleApprove}
                  disabled={approving || deleting}
                  sx={isPhone ? { height: 48, flex: '1.4 1 auto' } : undefined}
                >
                  {approving ? t('currentAction.approving') : t('currentAction.approve')}
                </Button>
              ) : (
                // Editing a waiting action would restart it under the child, so only here
                <Button
                  size={isPhone ? 'large' : 'small'}
                  startIcon={<EditIcon />}
                  variant="outlined"
                  disabled={deleting}
                  onClick={handleEditClick}
                  sx={isPhone ? { height: 48, flex: '1 1 auto' } : undefined}
                >
                  {t('currentAction.editSettings')}
                </Button>
              )}
              <Button
                size={isPhone ? 'large' : waiting ? 'medium' : 'small'}
                startIcon={<DeleteIcon />}
                variant="outlined"
                color="error"
                onClick={handleRemoveClick}
                disabled={deleting || approving}
                sx={isPhone ? { height: 48, flex: '1 1 auto' } : undefined}
              >
                {t('currentAction.remove')}
              </Button>
            </Box>
          </Box>

          {noticeLine(identity)}

          {waiting && (
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>{t('currentAction.awaitingTitle')}</AlertTitle>
              {t('currentAction.awaitingMessage', {
                action: action ? action.name : currentAction.actionId,
                device: deviceName,
                time: formatClockTime(currentAction.doneAt ?? Date.now()),
              })}
            </Alert>
          )}

          {waiting && approveError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('currentAction.approveError', { device: deviceName })}
            </Alert>
          )}

          {currentAction.stayUntilParentApproves && offline && lastSeen !== null && (
            <Alert severity="info" icon={<WifiOffIcon />} sx={{ mb: 2 }}>
              {t(waiting ? 'currentAction.deviceOffline' : 'currentAction.deviceOfflineActive', {
                device: deviceName,
                time: formatClockTime(lastSeen),
              })}
            </Alert>
          )}

          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}

          {/* Main content with thumbnail and details side by side */}
          <Box display="flex" gap={2} alignItems="flex-start">
            {/* Action thumbnail picture */}
            {(action?.thumbnailUrl || action?.pictureUrl) ? (
              imageLoading ? (
                <Skeleton variant="rectangular" width={120} height={120} sx={{ borderRadius: 1, flexShrink: 0 }} />
              ) : imageUrl && !imageError ? (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={action.name}
                  sx={{
                    width: 120,
                    height: 120,
                    objectFit: 'cover',
                    borderRadius: 1,
                    flexShrink: 0,
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'grey.200',
                    borderRadius: 1,
                    flexShrink: 0,
                  }}
                >
                  <ImageIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                </Box>
              )
            ) : null}

            {/* Action details */}
            <Box flex={1} minWidth={0}>
              {/* Action name */}
              <Typography variant="h5" gutterBottom noWrap>
                {action ? action.name : currentAction.actionId}
              </Typography>

              {/* Relative start time; while waiting, the alert above carries the time */}
              {!waiting && (
                <Typography variant="body1" color="primary" gutterBottom fontWeight="medium">
                  {relativeTime}
                </Typography>
              )}

              {/* Stay condition badges */}
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {currentAction.stayUntilParentApproves && (
                  <Chip
                    label={t('currentAction.parentApproval')}
                    color="warning"
                    size="small"
                  />
                )}
                {currentAction.stayUntilChecked && (
                  <Chip
                    label={t('currentAction.childCheck')}
                    color="info"
                    size="small"
                  />
                )}
                {currentAction.duration > 0 && (
                  <Chip
                    label={t('currentAction.duration', { minutes: currentAction.duration, plural: currentAction.duration === 1 ? '' : 's' })}
                    color="success"
                    size="small"
                  />
                )}
                {!currentAction.stayUntilParentApproves &&
                 !currentAction.stayUntilChecked &&
                 currentAction.duration === 0 && (
                  <Chip
                    label={t('currentAction.noCompletionConditions')}
                    color="default"
                    size="small"
                  />
                )}
              </Box>

              {currentAction.stayUntilParentApproves && !waiting && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {t('currentAction.approvalHint', { device: deviceName })}
                </Typography>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Edit Action Dialog */}
      {action && currentAction && !waiting && (
        <SendActionDialog
          open={editDialogOpen}
          onClose={handleEditClose}
          deviceId={deviceId}
          action={action}
          editMode={true}
          currentAction={currentAction}
        />
      )}

      {/* Delete confirmation dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t('currentAction.removeActionDialog')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('currentAction.removeActionConfirm')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            {t('currentAction.remove')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
